/**
 * RoadsListPanel - Panel de vías base del IGN en el RightPanel
 *
 * @version 1.0
 * @created 2026-03-01
 * @module core/layouts/panels/RightPanel
 *
 * @description
 * Muestra la lista de vías base importadas del IGN Argentina.
 * Permite seleccionar vías para resaltarlas en el mapa (bidireccional).
 *
 * @remarks
 * - Se abre cuando el usuario hace clic en "Vías Base (IGN)" en el LeftPanel
 * - Muestra atributos básicos del IGN: rtn (ruta), rst (revestimiento), typ (tipo), hct (categoría)
 * - Selección bidireccional: panel ↔ mapa
 * - Capa de solo lectura (no editable)
 */

import { X } from 'lucide-react';
import { useBaseNetwork } from '../../../spatial';
import './roads-list-panel.css';

/**
 * Props del componente RoadsListPanel
 */
export interface RoadsListPanelProps {
  /**
   * Callback cuando el usuario hace hover sobre una vía
   */
  onRoadHover: (roadId: string | null) => void;

  /**
   * Callback cuando el usuario selecciona una vía
   */
  onRoadSelect: (roadId: string | null) => void;

  /**
   * Callback cuando el usuario cierra el panel
   */
  onClose: () => void;
}

/**
 * Mapeo de códigos de revestimiento a labels legibles
 */
const SURFACE_TYPE_LABELS: Record<number, string> = {
  1: 'Pavimentada',
  2: 'Ripio',
  3: 'Tierra',
};

/**
 * Mapeo de códigos de tipo a labels legibles
 */
const ROAD_TYPE_LABELS: Record<number, string> = {
  40: 'Ruta estándar',
  41: 'Ruta principal',
};

/**
 * Mapeo de categorías a labels legibles
 */
const CATEGORY_LABELS: Record<string, string> = {
  autopista_010: 'Autopista 010',
  autopista_050: 'Autopista 050',
  nacional: 'Nacional',
  provincial: 'Provincial',
  terciaria: 'Terciaria',
};

/**
 * Panel de vías base del IGN en el RightPanel.
 *
 * @remarks
 * Muestra lista de vías con selección bidireccional (panel ↔ mapa).
 */
export function RoadsListPanel({
  onRoadHover,
  onRoadSelect,
  onClose,
}: RoadsListPanelProps): JSX.Element {
  const {
    roads,
    selectedRoadId,
    hoveredRoadId,
  } = useBaseNetwork();

  /**
   * Handler para seleccionar una vía
   */
  const handleRoadClick = (roadId: string): void => {
    onRoadSelect(selectedRoadId === roadId ? null : roadId);
  };

  /**
   * Handler para hover sobre una vía
   */
  const handleRoadHover = (roadId: string | null): void => {
    onRoadHover(roadId);
  };

  return (
    <div className="roads-list-panel">
      {/* Header del panel */}
      <header className="roads-list-panel-header">
        <div className="roads-list-panel-title-group">
          <h2 className="roads-list-panel-title">Vías Base</h2>
          <span className="roads-list-panel-subtitle">IGN Argentina</span>
        </div>
        <button
          className="roads-list-panel-close-btn"
          onClick={onClose}
          title="Cerrar panel"
          aria-label="Cerrar panel de vías"
        >
          <X size={16} />
        </button>
      </header>

      {/* Contenido: tabla de vías */}
      <div className="roads-list-panel-content">
        {roads.length === 0 ? (
          <div className="roads-list-panel-empty">
            <p>No hay vías base importadas</p>
          </div>
        ) : (
          <div className="roads-list-panel-table-wrapper">
            <table className="roads-list-panel-table">
              <thead>
                <tr>
                  <th>Categoría</th>
                  <th>Ruta</th>
                  <th>Tipo</th>
                  <th>Revestimiento</th>
                  <th>Cat. Circulación</th>
                </tr>
              </thead>
              <tbody>
                {roads.map((road) => (
                  <tr
                    key={road.id}
                    className={`
                      roads-list-panel-row
                      ${selectedRoadId === road.id ? 'selected' : ''}
                      ${hoveredRoadId === road.id ? 'hovered' : ''}
                    `}
                    onClick={() => handleRoadClick(road.id)}
                    onMouseEnter={() => handleRoadHover(road.id)}
                    onMouseLeave={() => handleRoadHover(null)}
                    title={`Click para ${selectedRoadId === road.id ? 'deseleccionar' : 'seleccionar'}`}
                  >
                    <td className="roads-list-panel-cell roads-list-panel-cell--category">
                      {CATEGORY_LABELS[road.category] || road.category}
                    </td>
                    <td className="roads-list-panel-cell roads-list-panel-cell--route">
                      {road.rtn || '-'}
                    </td>
                    <td className="roads-list-panel-cell roads-list-panel-cell--type">
                      {road.typ ? (ROAD_TYPE_LABELS[road.typ] || road.typ) : '-'}
                    </td>
                    <td className="roads-list-panel-cell roads-list-panel-cell--surface">
                      {road.rst ? (SURFACE_TYPE_LABELS[road.rst] || road.rst) : '-'}
                    </td>
                    <td className="roads-list-panel-cell roads-list-panel-cell--hct">
                      {road.hct ?? '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer con contador */}
        {roads.length > 0 && (
          <footer className="roads-list-panel-footer">
            <span className="roads-list-panel-count">
              {roads.length} vías totales
            </span>
          </footer>
        )}
      </div>
    </div>
  );
}
